export interface ScrollControl {
  cleanup: () => void;
  /** Externally pause/resume the auto-scroll (e.g. while a dropdown is open). */
  setPaused: (paused: boolean) => void;
}

/**
 * Pointer-driven drag-to-scroll for the card row, plus arrow buttons and an
 * optional gentle auto-scroll. Uses Pointer Events so the same code handles
 * mouse and touch. Returns a cleanup function + an external pause control.
 */
export function enableScrolling(
  scroller: HTMLElement,
  prev: HTMLElement,
  next: HTMLElement,
  opts: { autoscroll: boolean }
): ScrollControl {
  let down = false;
  let startX = 0;
  let startScroll = 0;
  let moved = false;
  let externalPaused = false;

  const onDown = (e: PointerEvent) => {
    down = true;
    moved = false;
    startX = e.clientX;
    startScroll = scroller.scrollLeft;
    scroller.classList.add("wc-dragging");
  };
  const onMove = (e: PointerEvent) => {
    if (!down) return;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > 3) moved = true;
    scroller.scrollLeft = startScroll - dx;
  };
  const onUp = () => {
    down = false;
    scroller.classList.remove("wc-dragging");
  };
  // Suppress click (card expand) right after a drag.
  const onClickCapture = (e: Event) => {
    if (moved) {
      e.stopPropagation();
      e.preventDefault();
      moved = false;
    }
  };

  scroller.addEventListener("pointerdown", onDown);
  scroller.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  scroller.addEventListener("click", onClickCapture, true);

  const step = () => Math.max(160, scroller.clientWidth * 0.6);
  const onPrev = () => scroller.scrollBy({ left: -step(), behavior: "smooth" });
  const onNext = () => scroller.scrollBy({ left: step(), behavior: "smooth" });
  prev.addEventListener("click", onPrev);
  next.addEventListener("click", onNext);

  const updateArrows = () => {
    const max = scroller.scrollWidth - scroller.clientWidth - 1;
    prev.hidden = scroller.scrollLeft <= 1;
    next.hidden = scroller.scrollLeft >= max;
  };
  scroller.addEventListener("scroll", updateArrows);
  updateArrows();

  // Optional auto-scroll that pauses on hover/drag and reverses at the ends.
  let raf = 0;
  let dir = 1;
  let paused = false;
  const reduce =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const onEnter = () => (paused = true);
  const onLeave = () => (paused = false);

  if (opts.autoscroll && !reduce) {
    scroller.addEventListener("pointerenter", onEnter);
    scroller.addEventListener("pointerleave", onLeave);
    // Keep a float accumulator: browsers round scrollLeft to whole pixels, so
    // adding < 1px per frame to scrollLeft directly never advances (it floors
    // back). Accumulate in `pos` and assign it each frame instead.
    let pos = scroller.scrollLeft;
    const speed = 0.6; // px per frame (~36px/s at 60fps)
    const tick = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      if (!paused && !externalPaused && !down && max > 1) {
        // If the user moved the scroller (drag/arrows/wheel), resync.
        if (Math.abs(scroller.scrollLeft - pos) > 2) pos = scroller.scrollLeft;
        pos += speed * dir;
        if (pos >= max) {
          pos = max;
          dir = -1;
        } else if (pos <= 0) {
          pos = 0;
          dir = 1;
        }
        scroller.scrollLeft = pos;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  }

  const cleanup = () => {
    scroller.removeEventListener("pointerdown", onDown);
    scroller.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    scroller.removeEventListener("click", onClickCapture, true);
    prev.removeEventListener("click", onPrev);
    next.removeEventListener("click", onNext);
    scroller.removeEventListener("scroll", updateArrows);
    scroller.removeEventListener("pointerenter", onEnter);
    scroller.removeEventListener("pointerleave", onLeave);
    if (raf) cancelAnimationFrame(raf);
  };

  return {
    cleanup,
    setPaused: (v: boolean) => {
      externalPaused = v;
    },
  };
}
